import { useTranslation } from 'react-i18next';
import { TransactionStatusBadge } from '@/components/features/transactions/status-badge';
import { formatDate, formatCents } from '@/lib/utils';
import type { AdminTraceResponse } from '@/types/admin-trace';
import type { TransactionStatus } from '@/types/admin-transaction';

/**
 * TX-Kopf + Identifiers + Trace-Meta (Spec 143 §5.3).
 * Alle Felder defensiv optional — Sparse-TXs liefern nur Teilmengen (Trap §8.8).
 */
export function TraceSummary({ trace }: { trace: AdminTraceResponse }) {
  const { t } = useTranslation('transactions');
  const tx = trace.transaction ?? {};
  const identifiers = trace.identifiers ?? {};
  const meta = trace.meta ?? {};

  const status = typeof tx.status === 'string' ? tx.status : undefined;
  // Facade-Trace liefert bei Failed-TXs GrossAmount: 0, waehrend das TX-Objekt
  // den echten Betrag traegt (E2E-Befund 2026-06-11). 0 traegt keine
  // Forensik-Information und wirkt wie ein Null-Betrags-Kauf — unterdruecken;
  // der autoritative Betrag steht im Uebersicht-Tab daneben.
  const grossAmount =
    typeof tx.grossAmount === 'number' && tx.grossAmount > 0 ? tx.grossAmount : undefined;

  const identifierEntries = Object.entries(identifiers).filter(([, v]) => v != null && v !== '');

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="trace-summary">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {status && <TransactionStatusBadge status={status as TransactionStatus} />}
          {typeof tx.flowType === 'string' && <span className="text-sm">{tx.flowType}</span>}
          {grossAmount != null && (
            <span className="text-sm text-muted-foreground">{formatCents(grossAmount)}</span>
          )}
        </div>
        {typeof tx.createdAt === 'string' && (
          <p className="text-xs text-muted-foreground">
            {t('trace.summary.created')}: {formatDate(tx.createdAt)}
            {typeof tx.updatedAt === 'string' && (
              <> · {t('trace.summary.updated')}: {formatDate(tx.updatedAt)}</>
            )}
          </p>
        )}
        {(meta.dataSources?.length || meta.traceGeneratedAt || meta.queryDurationMs != null) && (
          <p className="text-xs text-muted-foreground" data-testid="trace-meta">
            {meta.dataSources && meta.dataSources.length > 0 && (
              <>{t('trace.summary.dataSources')}: {meta.dataSources.join(', ')}</>
            )}
            {meta.traceGeneratedAt && (
              <> · {t('trace.summary.generatedAt')}: {formatDate(meta.traceGeneratedAt)}</>
            )}
            {meta.queryDurationMs != null && <> · {meta.queryDurationMs} ms</>}
          </p>
        )}
      </div>
      {identifierEntries.length > 0 && (
        <div>
          <p className="mb-1 text-sm font-medium">{t('trace.summary.identifiers')}</p>
          <dl className="space-y-0.5">
            {identifierEntries.map(([key, value]) => (
              <div key={key} className="flex gap-2 text-xs">
                <dt className="w-40 shrink-0 text-muted-foreground">{key}</dt>
                <dd className="break-all font-mono">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
